import {
	useMutation,
	useQueryClient,
	type UseMutationResult,
} from "@tanstack/react-query";
import type { ApiClientError } from "./_errors";
import { apiJson } from "./_http";
import type {
	FamDetail,
	FamMember,
	FamRole,
	InviteMemberRequest,
	UpdateFamRequest,
	UpdateMemberRoleRequest,
} from "../types";

// Wire shape returned by POST /api/fams/:slug/invites → identity Worker. The
// `url` is what the PM copies and shares; the SPA never needs the raw token
// (verification + redemption is server-side).
export interface ClaimLinkMintResponse {
	token: string;
	url: string;
	expiresAt: number; // unix seconds
	role: FamRole;
	famId: string;
	famSlug: string | null;
}

export interface ClaimLinkMintRequest {
	role?: FamRole;
	ttlSeconds?: number;
}

// TanStack Query hooks for the fam-settings surface: rename / logo update,
// invite (direct-add) a new member by X handle, role change, and kick.
// All mutations go through the web-app's own Pages Functions at
// /api/fams/:slug/... which proxy to the fam services-cf Worker.

// --- Hooks -------------------------------------------------------------------

// useUpdateFam — PATCH /api/fams/:slug. Rename + logo updates (PM-only).
// On success, invalidates the fam viewer's query so the new name/logo shows
// up.
export function useUpdateFam(
	famSlug: string,
): UseMutationResult<FamDetail, ApiClientError, UpdateFamRequest> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: UpdateFamRequest) =>
			apiJson<FamDetail>(`/api/fams/${encodeURIComponent(famSlug)}`, {
				method: "PATCH",
				body: JSON.stringify(body),
			}),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ["fam", famSlug] });
			void qc.invalidateQueries({ queryKey: ["me", "fams"] });
		},
	});
}

// useUpdateMemberRole — PATCH /api/fams/:slug/members/:userId. PM-only.
// The fam service expects { role: "<FamRole>" }.
export function useUpdateMemberRole(
	famSlug: string,
): UseMutationResult<FamMember, ApiClientError, { userId: string; role: FamRole }> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ userId, role }) => {
			const body: UpdateMemberRoleRequest = { role };
			return apiJson<FamMember>(
				`/api/fams/${encodeURIComponent(famSlug)}/members/${encodeURIComponent(userId)}`,
				{ method: "PATCH", body: JSON.stringify(body) },
			);
		},
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ["fam", famSlug] });
		},
	});
}

// useInviteMember — POST /api/fams/:slug/members. PM direct-add by X handle.
// Defaults role to "member" if the caller doesn't specify.
export function useInviteMember(
	famSlug: string,
): UseMutationResult<FamMember, ApiClientError, InviteMemberRequest> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (body: InviteMemberRequest) =>
			apiJson<FamMember>(`/api/fams/${encodeURIComponent(famSlug)}/members`, {
				method: "POST",
				body: JSON.stringify(body),
			}),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ["fam", famSlug] });
		},
	});
}

// useMintClaimLink — POST /api/fams/:slug/invites. PM-only. Returns a signed
// claim-link URL the PM hands to a friend. The token is stateless (HMAC-only,
// no DB row); a new mint always yields a fresh token + URL, so the PM can
// rotate by simply minting again.
export function useMintClaimLink(
	famSlug: string,
): UseMutationResult<ClaimLinkMintResponse, ApiClientError, ClaimLinkMintRequest> {
	return useMutation({
		mutationFn: (body: ClaimLinkMintRequest) =>
			apiJson<ClaimLinkMintResponse>(
				`/api/fams/${encodeURIComponent(famSlug)}/invites`,
				{ method: "POST", body: JSON.stringify(body ?? {}) },
			),
	});
}

// useKickMember — DELETE /api/fams/:slug/members/:userId. PM-only.
export function useKickMember(
	famSlug: string,
): UseMutationResult<void, ApiClientError, { userId: string }> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ userId }) =>
			apiJson<void>(
				`/api/fams/${encodeURIComponent(famSlug)}/members/${encodeURIComponent(userId)}`,
				{ method: "DELETE" },
			),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ["fam", famSlug] });
		},
	});
}
